import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useTokenGetter } from "../auth";
import { AnnotationEditor } from "../components/AnnotationEditor";
import { apiUrl } from "../lib/api";
import { useDebouncedValue } from "../lib/hooks";
import { uploadImage, type UploadResult } from "../lib/upload";
import { trpc } from "../trpc";

type Language = "hindi" | "english";

/**
 * "Capture a doha": a signed-in reader photographs a couplet, sets its
 * language, optionally tags which doha it is, and then marks words on the
 * image in the AnnotationEditor.
 */
export function CapturePage() {
  const getToken = useTokenGetter();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [language, setLanguage] = useState<Language>("hindi");
  const [dohaSlug, setDohaSlug] = useState<string | null>(params.get("doha"));
  const [tagQuery, setTagQuery] = useState("");
  const debTag = useDebouncedValue(tagQuery.trim());

  // When a doha is tagged, surface photos it already has so people
  // reference the shared asset instead of piling up duplicates.
  const existing = trpc.dohas.get.useQuery(
    { slug: dohaSlug ?? "" },
    { enabled: dohaSlug !== null },
  );
  const existingCaptures = existing.data?.captures ?? [];
  const addToStudy = trpc.study.add.useMutation({
    onSuccess: () => {
      if (dohaSlug) navigate(`/doha/${dohaSlug}`);
    },
  });

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [upload, setUpload] = useState<UploadResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [captureId, setCaptureId] = useState<number | null>(null);

  const dohaSearch = trpc.dohas.search.useQuery(
    { q: debTag, limit: 12 },
    { enabled: debTag.length > 0 && dohaSlug === null },
  );

  const createCapture = trpc.captures.create.useMutation({
    onSuccess: (data) => setCaptureId(data.id),
  });

  // Revoke the previous object URL when it changes and on unmount.
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUpload(null);
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const token = await getToken();
      const result = await uploadImage(file, token);
      setUpload(result);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  function beginAnnotating() {
    if (!upload) return;
    createCapture.mutate({
      dohaSlug: dohaSlug ?? undefined,
      language,
      r2Key: upload.r2Key,
      width: upload.width,
      height: upload.height,
    });
  }

  if (captureId !== null && upload) {
    return (
      <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
        <AnnotationEditor
          captureId={captureId}
          mediaPath={previewUrl ?? `/media/${upload.r2Key}`}
          dohaSlug={dohaSlug}
        />
      </div>
    );
  }

  const canBegin = Boolean(upload) && !uploading;

  return (
    <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
      <p className="eyebrow text-ink-faint">Capture</p>
      <h1 className="mt-1 font-serif text-3xl text-ink">Photograph a doha</h1>
      <p className="mt-3 max-w-prose text-ink-soft">
        Snap the couplet you’re reading. Once it’s uploaded you can mark the
        words on it — linking each to the glossary, or flagging the ones that
        still need a definition.
      </p>

      {/* Step 1 — the photo */}
      <section className="mt-10">
        <h2 className="eyebrow text-ink-faint">1 · The photo</h2>
        {previewUrl ? (
          <div className="mt-3 rounded-2xl border border-line bg-paper-raised p-4">
            <div className="flex justify-center rounded-xl bg-paper-sunk p-3">
              <img
                src={previewUrl}
                alt="Doha to annotate"
                className="max-h-80 max-w-full rounded-lg"
              />
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-sm text-ink-faint">
                {uploading
                  ? "Uploading…"
                  : upload
                    ? "Uploaded"
                    : uploadError
                      ? "Upload failed"
                      : "Ready"}
              </span>
              <label className="cursor-pointer rounded-full border border-line-strong px-4 py-2 text-sm text-ink transition-colors hover:bg-paper-sunk">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={onFileChange}
                />
                Choose another
              </label>
            </div>
          </div>
        ) : (
          <label className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-line-strong bg-paper-raised px-6 py-12 text-center transition-colors hover:border-terracotta/60">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={onFileChange}
            />
            <span className="font-serif text-lg text-ink">
              Take or choose a photo
            </span>
            <span className="mt-1 text-sm text-ink-faint">
              A clear shot of the couplet works best.
            </span>
          </label>
        )}
        {uploadError ? (
          <p className="mt-2 text-sm text-terracotta">{uploadError}</p>
        ) : null}
      </section>

      {/* Step 2 — language */}
      <section className="mt-10">
        <h2 className="eyebrow text-ink-faint">2 · Language on the page</h2>
        <div className="mt-3 inline-flex gap-2">
          {(["hindi", "english"] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => setLanguage(lang)}
              className={
                language === lang
                  ? "rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper capitalize"
                  : "rounded-full border border-line-strong px-5 py-2 text-sm text-ink transition-colors hover:bg-paper-sunk capitalize"
              }
            >
              {lang}
            </button>
          ))}
        </div>
      </section>

      {/* Step 3 — tag the doha (optional) */}
      <section className="mt-10">
        <h2 className="eyebrow text-ink-faint">3 · Which doha? (optional)</h2>
        {dohaSlug ? (
          <>
            <div className="mt-3 inline-flex items-center gap-3 rounded-full border border-terracotta bg-terracotta-wash px-4 py-2">
              <span className="font-mono text-sm text-terracotta">
                {dohaSlug}
              </span>
              <button
                type="button"
                onClick={() => setDohaSlug(null)}
                className="text-sm text-terracotta/80 transition-colors hover:text-terracotta"
                aria-label="Clear tagged doha"
              >
                ✕
              </button>
            </div>

            {existingCaptures.length > 0 ? (
              <div className="mt-4 rounded-2xl border border-terracotta/40 bg-terracotta-wash/40 p-4">
                <p className="font-serif text-lg text-ink">
                  {dohaSlug} has already been captured (
                  {existingCaptures.length}).
                </p>
                <p className="mt-1 text-sm text-ink-soft">
                  Reference the existing photos instead of adding a duplicate —
                  or add your own below if it’s a clearer shot.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {existingCaptures.slice(0, 4).map((c) => (
                    <Link
                      key={c.id}
                      to={`/capture/${c.id}`}
                      className="overflow-hidden rounded-lg border border-line bg-paper-sunk"
                    >
                      <img
                        src={apiUrl(c.mediaPath)}
                        alt="Existing capture"
                        className="aspect-[4/3] w-full object-cover"
                      />
                    </Link>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={addToStudy.isPending}
                  onClick={() => addToStudy.mutate({ dohaSlug })}
                  className="mt-3 rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {addToStudy.isPending ? "Adding…" : "Reference this doha"}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="mt-3 max-w-md">
            <input
              type="search"
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              placeholder="Search a reference — e.g. M2C5D3"
              aria-label="Search for a doha"
              className="w-full rounded-xl border border-line-strong bg-paper-raised px-4 py-2.5 font-mono text-ink outline-none transition focus:border-terracotta/60 placeholder:text-ink-faint/70"
            />
            {debTag.length > 0 ? (
              <div className="mt-3 overflow-hidden rounded-xl border border-line bg-paper-raised">
                {dohaSearch.isLoading ? (
                  <p className="px-4 py-3 text-sm text-ink-faint">Searching…</p>
                ) : dohaSearch.data && dohaSearch.data.length > 0 ? (
                  <ul className="divide-y divide-line">
                    {dohaSearch.data.map((d) => (
                      <li key={d.slug}>
                        <button
                          type="button"
                          onClick={() => {
                            setDohaSlug(d.slug);
                            setTagQuery("");
                          }}
                          className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-paper-sunk"
                        >
                          <span className="font-mono text-ink">{d.slug}</span>
                          <span className="text-xs text-ink-faint">
                            Mandal {d.mandal} · Ch {d.chapter} · Doha {d.doha}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-4 py-3 text-sm text-ink-faint">
                    No doha matches “{debTag}”.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        )}
      </section>

      <div className="mt-12 flex items-center gap-4 border-t border-line pt-6">
        <button
          type="button"
          disabled={!canBegin || createCapture.isPending}
          onClick={beginAnnotating}
          className="rounded-full bg-ink px-6 py-2.5 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {createCapture.isPending ? "Preparing…" : "Begin annotating"}
        </button>
        {!upload ? (
          <span className="text-sm text-ink-faint">
            Upload a photo to continue.
          </span>
        ) : null}
        {createCapture.error ? (
          <span className="text-sm text-terracotta">
            {createCapture.error.message}
          </span>
        ) : null}
      </div>
    </div>
  );
}
